// Resolving a key to a string. Pure, and deliberately knows nothing about
// React: the wording of this game is worth testing without rendering a route,
// which is why eventLog.js and leaveGuard.js are their own files too.

const PLACEHOLDER = /\{\{(\w+)\}\}/g;

/**
 * The catalog entry for a key, honouring the two plural forms.
 *
 * An exact key always wins. Only when there isn't one, and a `count` is in
 * play, do `_one` / `_other` come into it — so a key can be pluralised later
 * without touching its call sites.
 */
function lookup(catalog, key, params) {
  if (!catalog) return undefined;
  if (typeof catalog[key] === 'string') return catalog[key];

  const count = params?.count;
  if (typeof count !== 'number') return undefined;

  // English and Greek share this rule. A language that needs more forms
  // changes this one function, not 400 call sites.
  return catalog[count === 1 ? `${key}_one` : `${key}_other`];
}

/**
 * `key` rendered in `catalog`, falling back to `fallback` and then to the key.
 *
 * Showing the key is the worst case and it is still a diagnosis: a screen
 * reading `bidding.ready` says which string is missing, where a blank button
 * says only that something is broken.
 *
 * @param {object} catalog   The active locale's strings.
 * @param {object} fallback  English, always.
 * @param {string} key       Dot-notation key, e.g. `bidding.ready`.
 * @param {object} [params]  Values for `{{name}}` placeholders.
 *
 * @return {string}
 */
export function translate(catalog, fallback, key, params) {
  const template = lookup(catalog, key, params)
    ?? lookup(fallback, key, params)
    ?? key;

  if (!params) return template;

  // An unfilled placeholder is left standing rather than blanked: it shows a
  // translator exactly which value their string asked for and did not get.
  return template.replace(PLACEHOLDER, (whole, name) =>
    (name in params ? String(params[name]) : whole));
}
